import { apiDelete, apiGet, apiPost, apiPut } from '@/utils/api';
import getPath from '@/utils/getPath';
import { handleApiError } from '@/utils/errorHandler';

export const strategyApi = {
  // ---------------------------------------------------------------------------
  // Strategy
  // ---------------------------------------------------------------------------

  /**
   * Fetches a work strategy and its executions.
   * @apiRoute GET '/strategy/<strategy_id>'
   */
  async getStrategy(strategyId, on = null) {
    try {
      const params = on ? new URLSearchParams({ on }) : null;
      const response = await apiGet(getPath(`strategy/${strategyId}`), params);
      return response.data;
    } catch (err) {
      await handleApiError(err, 'getStrategy');
    }
  },

  /**
   * Creates a draft for a work strategy.
   * @apiRoute POST '/strategy/<strategy_id>/drafts'
   */
  async createDraft(workStrategyId) {
    try {
      const response = await apiPost(
        getPath(`strategy/${workStrategyId}/drafts`)
      );
      return response.data;
    } catch (err) {
      await handleApiError(err, 'createDraft');
    }
  },

  /**
   * Deletes a draft for a work strategy.
   * @apiRoute DELETE `strategy/<strategy_id>/drafts/<draftId>`
   */
  async deleteDraft(workStrategyId, draftId) {
    try {
      const response = await apiDelete(
        getPath(`strategy/${workStrategyId}/drafts/${draftId}`)
      );
      return response.data;
    } catch (err) {
      await handleApiError(err, 'deleteDraft');
    }
  },

  /**
   * Deletes a draft on page unload. Uses fetch with keepalive so the request
   * completes even after the page closes.
   * @apiRoute DELETE `strategy/<strategy_id>/drafts/<draftId>`
   */
  deleteDraftOnUnload(workStrategyId, draftId) {
    const token = sessionStorage.getItem('id_token');
    if (!token) return;

    fetch(getPath(`strategy/${workStrategyId}/drafts/${draftId}`), {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      keepalive: true,
    });
  },

  /**
   * Saves (publishes) a work strategy draft.
   * @apiRoute PUT `strategy/<strategy_id>`
   */
  async saveStrategy(workStrategyId, payload) {
    try {
      const response = await apiPut(
        getPath(`strategy/${workStrategyId}`),
        payload
      );
      return response.data;
    } catch (err) {
      await handleApiError(err, 'saveStrategy');
    }
  },

  // ---------------------------------------------------------------------------
  // Worklists
  // ---------------------------------------------------------------------------

  /**
   * Fetches worklists for a strategy execution.
   * @apiRoute GET `strategy/<strategy_id>/worklists`
   */
  async getWorklists(strategyId, params) {
    try {
      const response = await apiGet(
        getPath(`strategy/${strategyId}/worklists`),
        params
      );
      return response.data;
    } catch (err) {
      await handleApiError(
        err,
        'getWorklists',
        true,
        'Error Fetching Work List'
      );
    }
  },

  /**
   * Submits a worklist change (create, edit, or delete) against a draft.
   * @apiRoute POST `strategy/<strategy_id>/drafts/<draft_id>/changes`
   */
  async submitWorklistChange(workStrategyId, draftId, body) {
    try {
      const response = await apiPost(
        getPath(`strategy/${workStrategyId}/drafts/${draftId}/changes`),
        body
      );
      return response.data;
    } catch (err) {
      await handleApiError(err, 'submitWorklistChange');
    }
  },

  /**
   * Fetches a preview of work items for a worklist.
   * @apiRoute GET `strategy/<strategy_id>/worklists/<work_list_id>/preview`
   */
  async previewWorklist(strategyId, workListId) {
    try {
      const response = await apiGet(
        getPath(`strategy/${strategyId}/worklists/${workListId}/preview`)
      );
      return response.data;
    } catch (err) {
      await handleApiError(err, 'previewWorklist', true);
    }
  },

  // ---------------------------------------------------------------------------
  // Inflow / Outflow
  // ---------------------------------------------------------------------------

  /**
   * Fetches inflow/outflow overview data.
   * @apiRoute GET `strategy/1/inoutflow/overview`
   */
  async getInflowOutflowOverview(params) {
    try {
      const response = await apiGet(
        getPath(`strategy/1/inoutflow/overview`),
        params
      );
      return response.data;
    } catch (err) {
      await handleApiError(err, 'getInflowOutflowOverview');
    }
  },

  /**
   * Fetches inflow/outflow activity (top increases/decreases).
   * @apiRoute GET `strategy/1/inoutflow/activity`
   */
  async getInflowOutflowActivity() {
    try {
      const response = await apiGet(getPath(`strategy/1/inoutflow/activity`));
      return response.data;
    } catch (err) {
      await handleApiError(err, 'getInflowOutflowActivity');
    }
  },

  /**
   * Fetches inflow/outflow detail records.
   * @apiRoute GET `strategy/1/inoutflow/detail`
   */
  async getInflowOutflowDetails(params) {
    try {
      const response = await apiGet(
        getPath(`strategy/1/inoutflow/detail`),
        params
      );
      return response.data;
    } catch (err) {
      await handleApiError(err, 'getInflowOutflowDetails');
    }
  },

  /**
   * Fetches worklists for inflow/outflow filtering.
   * @apiRoute GET `strategy/1/inoutflow/worklists`
   */
  async getInflowOutflowWorklists(params) {
    try {
      const response = await apiGet(
        getPath(`strategy/1/inoutflow/worklists`),
        params
      );
      return response.data;
    } catch (err) {
      await handleApiError(err, 'getInflowOutflowWorklists');
    }
  },

  // ---------------------------------------------------------------------------
  // Settings
  // ---------------------------------------------------------------------------

  /**
   * Fetches the grouping setting for a work strategy.
   * @apiRoute GET `strategy/<work_strategy_id>/settings/grouping`
   */
  async getWorkStrategySettings(workStrategyId) {
    try {
      const response = await apiGet(
        getPath(`strategy/${workStrategyId}/settings/grouping`)
      );
      return response.data;
    } catch (err) {
      await handleApiError(
        err,
        'getWorkStrategySettings',
        true,
        'Failed to load work strategy settings'
      );
    }
  },

  /**
   * Updates the grouping setting for a work strategy.
   * @apiRoute PUT `strategy/<work_strategy_id>/settings/grouping`
   */
  async updateWorkStrategySettings(workStrategyId, allocId) {
    try {
      const response = await apiPut(
        getPath(`strategy/${workStrategyId}/settings/grouping`),
        { alloc_id: allocId }
      );
      return response.data;
    } catch (err) {
      await handleApiError(
        err,
        'updateWorkStrategySettings',
        true,
        'Failed to update work strategy setting'
      );
    }
  },

  // ---------------------------------------------------------------------------
  // Dev / QA utilities
  // ---------------------------------------------------------------------------

  /**
   * Tests worklist criteria against current work items.
   * @apiRoute POST `utils/testCriteria`
   */
  async testCriteria(query) {
    try {
      const response = await apiPost(getPath(`utils/testCriteria`), {
        criteria_json: query,
      });
      return response.data;
    } catch (err) {
      await handleApiError(err, 'testCriteria', true);
    }
  },

  /**
   * Unlocks the game plan (dev/eng/test only).
   * @apiRoute POST `utils/unlockGameplan`
   */
  async unlockGameplan() {
    try {
      const response = await apiPost(getPath(`utils/unlockGameplan`));
      return response.data;
    } catch (err) {
      await handleApiError(err, 'unlockGameplan', true);
    }
  },

  /**
   * Triggers a work strategy recalculation (dev only).
   * @apiRoute POST `utils/calcWorkStrat`
   */
  async calcWorkStrat() {
    try {
      const response = await apiPost(getPath(`utils/calcWorkStrat`));
      return response.data;
    } catch (err) {
      await handleApiError(err, 'calcWorkStrat', true);
    }
  },
};
